"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { PhoneCall, Calendar, ArrowLeft, ShieldCheck, Clock, MapPin } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const trustBadges = [{
  id: 1,
  title: "24/7 Rapid Response",
  subtitle: "In Morrow & surrounding areas"
}, {
  id: 2,
  title: "Licensed & Insured",
  subtitle: "Trusted local plumbers"
}, {
  id: 3,
  title: "Upfront Pricing",
  subtitle: "No surprise fees"
}];
export default function MorrowClosing() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="morrowclosingcta" data-nav="light" className="relative bg-background py-16 sm:py-20 lg:py-24 overflow-hidden border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 max-w-5xl">
        <motion.div initial={shouldReduceMotion ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: "easeOut"
      }} className="bg-card text-card-foreground border border-border rounded-2xl p-8 sm:p-12 lg:p-14 shadow-lg text-center relative overflow-hidden">
          {/* Subtle decorative background glow */}
          <div className="absolute -top-24 -right-24 w-72 h-72 bg-accent/10 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-72 h-72 bg-primary/10 rounded-full blur-3xl pointer-events-none" />

          {/* Location badge */}
          <div className="flex justify-center mb-4">
            <Badge variant="secondary" className="px-3.5 py-1 text-xs sm:text-sm font-medium gap-1.5 rounded-full">
              <MapPin className="size-3.5 text-accent-foreground" />
              Serving Morrow, GA & Clayton County
            </Badge>
          </div>

          {/* Heading and subtext */}
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground max-w-2xl mx-auto">
            Need a Plumber in Morrow? Contact Us Today
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed">
            Emergency plumbing issues, water heater leaks, or drain problems — our licensed specialists are ready to respond promptly across Morrow.
          </p>

          {/* Action buttons */}
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button asChild size="lg" className="w-full sm:w-auto text-base font-semibold px-7 py-6 rounded-xl shadow-md gap-2.5">
              <a href="tel:4708479076">
                <PhoneCall className="size-5" />
                Call (470) 847-9076
              </a>
            </Button>

            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto text-base font-semibold px-7 py-6 rounded-xl border-border hover:bg-secondary gap-2.5">
              <Link to="/contact/">
                <SafeHydrate><Calendar className="size-5" /></SafeHydrate>
                Request Service Online
              </Link>
            </Button>
          </div>

          {/* Trust Highlights */}
          <div className="mt-10 pt-8 border-t border-border grid grid-cols-1 sm:grid-cols-3 gap-6 text-left sm:text-center">
            {trustBadges.map((badge, i) => <div key={badge.id} data-index={i} className="flex sm:flex-col items-center sm:items-center gap-3">
                <div className="size-10 rounded-full bg-secondary flex items-center justify-center shrink-0 text-primary">
                  {badge.id === 1 && <Clock className="size-5" />}
                  {badge.id === 2 && <ShieldCheck className="size-5" />}
                  {badge.id === 3 && <MapPin className="size-5" />}
                </div>
                <div>
                  <div className="font-semibold text-sm text-foreground">{badge.title}</div>
                  <div className="text-xs text-muted-foreground">{badge.subtitle}</div>
                </div>
              </div>)}
          </div>

          {/* Navigation Hub Link */}
          <div className="mt-8 flex justify-center">
            <Link to="/service-areas/" className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors group">
              <ArrowLeft className="size-4 transition-transform group-hover:-translate-x-1" />
              <span>Back to all Service Areas</span>
            </Link>
          </div>
        </motion.div>
      </div>
    </section>;
}